import { useMemo } from 'react';
import { array, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createColumnHelper } from '@tanstack/react-table';
import {
    Body,
    Container,
    Divider,
    Grid,
    IconButton,
    IconName,
    Order,
    Stack,
    Table,
    TableProvider,
    Typography,
    useTableState,
} from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedDateTimeFromUtc, LocalizedSmallParagraph, LocalizedSubsectionTitle } from '@mews-commander/ui';
import { UserGroup, UserGroupAccessPolicyType, UserGroupEnterpriseResponse } from '@mews-commander/data-access';
import { LocalizedEnum, LocalizedText, useDialog } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { GroupSettings } from '../GroupSettings';

const propertyColumnHelper = createColumnHelper<UserGroupEnterpriseResponse>();

const usePropertiesColumns = () => [
    propertyColumnHelper.accessor('name', {
        id: 'name',
        header: () => <LocalizedText textKey={TextKey.PropertyName} />,
        cell: (props) => props.getValue(),
        enableSorting: true,
    }),
];

export const GeneralInfo = ({ group }: { group: UserGroup }) => {
    const [editGroupDialogProps, toggleEditGroupDialog] = useDialog();

    const propertiesColumns = usePropertiesColumns();

    const [tableState, setTableState] = useTableState<UserGroupEnterpriseResponse>({
        pagination: {
            pageSize: 25,
        },
    });

    const sortedEnterprises = useMemo(() => {
        const sortOrder = tableState.sort.name?.order;
        if (sortOrder == null) {
            return group.enterprises;
        }

        const orderByName: ord.Ord<UserGroupEnterpriseResponse> = pipe(
            sortOrder === Order.Descending ? ord.reverse(string.Ord) : string.Ord,
            ord.contramap(({ name }) => name)
        );

        return array.sortBy([orderByName])(group.enterprises || []);
    }, [tableState.sort.name, group.enterprises]);

    const totalRowCount = sortedEnterprises?.length || 0;

    return (
        <>
            <Stack vertical spacing="50">
                <Stack distribution="space-between" itemAlignment="center">
                    <LocalizedSubsectionTitle textKey={TextKey.Description} />
                    <IconButton icon={IconName.Edit} onClick={toggleEditGroupDialog} type="submit" />
                </Stack>
                <Typography textStyle="bodyMedium" as="div">
                    {group.description || UnicodeEntity.Dash}
                </Typography>
            </Stack>
            <Stack vertical spacing="200">
                <LocalizedSubsectionTitle textKey={TextKey.Profile} />
                <Divider />
                <Typography textStyle="bodyMedium" as="div">
                    <LearningPathDataItem>
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.LearningPath} />
                        {group.assignedLearningPath ? (
                            <LocalizedEnum prefix="LearningPath" value={group.assignedLearningPath} />
                        ) : (
                            UnicodeEntity.Dash
                        )}
                    </LearningPathDataItem>
                </Typography>
            </Stack>
            <Stack vertical spacing="300">
                <LocalizedSubsectionTitle textKey={TextKey.RolesAndPermissions} />
                <Stack vertical spacing="200">
                    {group.accessPolicy && (
                        <>
                            <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
                                <LocalizedSmallParagraph
                                    variant="secondary"
                                    textKey={group.accessPolicy.roles?.length ? TextKey.Roles : TextKey.Privileges}
                                />

                                <Stack vertical spacing="150">
                                    {group.accessPolicy.accessPolicyType === UserGroupAccessPolicyType.Admin ? (
                                        <LocalizedSmallParagraph textKey={TextKey.IsAdmin} />
                                    ) : null}

                                    {group.accessPolicy.roles?.map((item) => (
                                        <Body size="small" key={item.id}>
                                            {item.name}
                                        </Body>
                                    ))}

                                    {group.accessPolicy.accessPolicyType === UserGroupAccessPolicyType.NoAccess
                                        ? UnicodeEntity.Dash
                                        : null}
                                </Stack>
                            </Grid>
                            <Divider />
                        </>
                    )}

                    <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.ReceiveCustomerMessages} />
                        <LocalizedSmallParagraph
                            textKey={
                                group.accessPolicy?.enterpriseProfileOptions?.receiveCustomerMessages
                                    ? TextKey.Yes
                                    : TextKey.No
                            }
                        />
                    </Grid>
                    <Divider />

                    <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.ResetTwoFactorShort} />
                        <LocalizedSmallParagraph
                            textKey={
                                group.accessPolicy?.enterpriseProfileOptions?.resetUsersTwoFactorAuthentication
                                    ? TextKey.Yes
                                    : TextKey.No
                            }
                        />
                    </Grid>
                    <Divider />

                    <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.CashierPlural} />
                        <Stack vertical spacing="150">
                            {group.cashierExternalIdentifiers?.length
                                ? group.cashierExternalIdentifiers.map((item) => (
                                      <Body size="small" key={item}>
                                          {item}
                                      </Body>
                                  ))
                                : UnicodeEntity.Dash}
                        </Stack>
                    </Grid>
                    <Divider />
                </Stack>
            </Stack>
            <Stack vertical spacing="200">
                <LocalizedSubsectionTitle textKey={TextKey.Properties} />
                <TableProvider state={tableState} onStateChange={setTableState} columns={propertiesColumns}>
                    {sortedEnterprises?.length ? (
                        <Table
                            uniqueRowId="id"
                            data={sortedEnterprises}
                            amountOfRows={totalRowCount}
                            fixedFirstColumn
                        />
                    ) : (
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.NoPropertiesAssigned} />
                    )}
                </TableProvider>

                <Container padding={['300', '0']}>
                    <Stack distribution="stretch" vertical spacing="200">
                        <LocalizedSubsectionTitle textKey={TextKey.Activity} />
                        <Divider />
                        <ActivityLogItem
                            textKey={TextKey.UpdatedOn}
                            label={
                                group?.activityLog?.updatedAtUtc ? (
                                    <LocalizedDateTimeFromUtc utcString={group?.activityLog?.updatedAtUtc} />
                                ) : null
                            }
                        />
                        <ActivityLogItem textKey={TextKey.UpdatedBy} label={group?.activityLog?.updaterFullName} />
                    </Stack>
                </Container>
            </Stack>

            <GroupSettings group={group} {...editGroupDialogProps} />
        </>
    );
};

const ActivityLogItem = ({
    textKey,
    label = <>{UnicodeEntity.Dash}</>,
}: {
    textKey: Mews.TextKey;
    label: React.ReactNode;
}) => (
    <>
        <Grid columnGap="100" templateColumns="minmax(20%, min-content) 1fr">
            <LocalizedSmallParagraph variant="secondary" textKey={textKey} />
            <Body size="small">{label || UnicodeEntity.Dash}</Body>
        </Grid>
        <Divider />
    </>
);

const LearningPathDataItem = ({ children }: React.PropsWithChildren<{}>) => (
    <Stack distribution="stretch" vertical spacing="200">
        <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
            {children}
        </Grid>
        <Divider />
    </Stack>
);
